<script setup lang="ts">
import { computed, inject, provide, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useSyncStore } from '@/stores/sync'
import { useAuthStore } from '@/stores/auth'
import { usePermissionsStore } from '@/stores/permissions'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import { useFormsStore } from '@/stores/forms'
import { useToastStore } from '@/stores/toast'
import FosButton from '@/components/ui/FosButton.vue'

const props = defineProps<{
  formType: string
  title: string
  backRoute: string
  backLabel: string
  backParams?: Record<string, string>
}>()

const router = useRouter()
const syncStore = useSyncStore()
const authStore = useAuthStore()
const permissionsStore = usePermissionsStore()
const { opRole } = useCurrentOpRole()
const formsStore = useFormsStore()
const toast = useToastStore()
const incidentId = inject<string>('incidentId', '')

const canEdit = computed(() => permissionsStore.can('forms.edit', opRole.value))
provide('formsCanEdit', canEdit)

onMounted(() => {
  if (incidentId) formsStore.subscribeToForms(incidentId)
})

const saveState = computed<'saving' | 'error' | 'saved'>(() => {
  if (!syncStore.isOnline) return 'error'
  if (syncStore.pendingWrites > 0) return 'saving'
  return 'saved'
})

const exporting = ref(false)

async function handleExport() {
  if (!syncStore.isOnline) {
    toast.add('PDF export requires a network connection.', 'warning')
    return
  }
  exporting.value = true
  try {
    const token = await authStore.currentUser?.getIdToken()
    const res = await fetch(`/api/incidents/${incidentId}/forms/${props.formType}/export-pdf`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
    const body = await res.json()
    if (res.status === 403) {
      toast.add("You don't have permission to perform this action.", 'error')
      return
    }
    if (!res.ok || body.error) throw new Error(body.error?.message ?? 'Export failed')
    window.open(body.data.pdf_url, '_blank')
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Export failed'
    toast.add(message, 'error')
  } finally {
    exporting.value = false
  }
}
</script>

<template>
  <div class="h-full grid" style="grid-template-rows: 48px 1fr; min-height: 0;">
    <!-- Sub-toolbar -->
    <div class="flex items-center gap-2.5 px-4 bg-surface border-b border-border shrink-0">
      <button
        class="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.10em] uppercase text-outline hover:text-primary transition-colors bg-transparent border-none cursor-pointer p-0"
        @click="router.push({ name: backRoute, params: backParams })"
      >
        <span class="material-symbols-outlined text-[14px]">arrow_back</span>
        {{ backLabel }}
      </button>
      <span class="w-px h-[18px] bg-border shrink-0" />
      <span class="font-headline font-black text-[13px] uppercase tracking-[0.04em] text-on-surface">{{ title }}</span>
      <span class="flex-1" />

      <!-- Autosave pill -->
      <span
        :class="[
          'inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.06em] px-2 py-1 border rounded',
          saveState === 'saving' && 'text-amber-700 bg-amber-50 border-amber-300',
          saveState === 'error'  && 'text-red-700 bg-red-50 border-red-300',
          saveState === 'saved'  && 'text-green-700 bg-green-50 border-green-300',
        ]"
      >
        <span :class="['material-symbols-outlined text-[14px]', saveState === 'saving' && 'animate-spin']">
          {{ saveState === 'saving' ? 'sync' : saveState === 'error' ? 'error_outline' : 'check' }}
        </span>
        {{ saveState === 'saving' ? 'Saving…' : saveState === 'error' ? 'Sync failed' : 'All changes saved' }}
      </span>

      <!-- Export PDF -->
      <FosButton v-if="!syncStore.isOnline" variant="secondary" size="sm" disabled>
        <span class="material-symbols-outlined text-[14px] mr-1">picture_as_pdf</span>
        Export PDF
      </FosButton>
      <FosButton v-else variant="primary" size="sm" :disabled="exporting" @click="handleExport">
        <span v-if="exporting" class="w-3 h-3 border-2 border-white/30 border-t-white animate-spin inline-block mr-1" />
        <span v-else class="material-symbols-outlined text-[14px] mr-1">picture_as_pdf</span>
        {{ exporting ? 'Generating…' : 'Export PDF' }}
      </FosButton>
    </div>

    <!-- Scrollable content -->
    <div class="overflow-y-auto min-h-0">
      <slot />
    </div>
  </div>
</template>
